export default {
  // 集成指南页面
  title: '分销集成文档',
  selectPlatform: '选择你的网站平台',
  helpAndFeedback: '帮助与反馈',
  setupCompleted: '集成已完成',
  noCampaignId: '缺少 campaignId 或 clientId',

  // 平台选择
  platforms: {
    selfHosted: '自托管',
    shopify: 'Shopify',
  },

  // 自托管集成指南
  selfHosted: {
    overview: {
      title: '1. 概览',
      description:
        '本指南将带你集成 Aha 的追踪系统，实现平台（Head）、达人（分销/联盟成员）与商家（品牌）之间的精准归因与顺畅分成。',
      keyGoals: '本指南的核心目标：',
      goals: [
        '提供清晰、对开发者友好的集成示例；',
        '同时支持前端（Client-Side）与后端（Server-Side）两种集成方式；',
        '确保支付归因准确，并自动完成收益分配。',
      ],
    },
    prerequisites: {
      title: '2. 前置条件',
      table: {
        item: '项目',
        details: '说明',
      },
      items: {
        clientId: 'Client ID',
        apiKey: 'API Key（服务器端）',
        apiKeyDesc:
          '用于服务端调用的 Aha API Key（请妥善保密）。可在此生成你的 Key：',
        orderId: 'AHA_ORDER_ID',
        orderIdDesc:
          '当用户通过分销链接进入时，URL 会附带该 ID。请在支付流程中保持该值不丢失，以确保归因准确。',
      },
    },
    embedScript: {
      title: '3. 嵌入追踪脚本',
      description: '将以下脚本添加至 {everyPage} 的 {headTag} 标签内：',
      everyPage: '所有参与分销支付或转化路径的页面：',
      warning1:
        '⚠️ 请原样使用官方脚本。擅自修改或更换 Client ID 可能导致追踪失败。',
      warning2:
        '请确保落地页、支付页与成功页均已接入脚本，以便正确归因与分成。',
    },
    integration: {
      title: '4. 集成方式',
      clientSide: {
        title: '4.1 前端集成（Client-Side）',
        subtitle: '示例：在支付前/支付成功回调后上报事件',
        description:
          '可在用户点击“支付”按钮时触发，或在支付成功的回调中触发。',
      },
      serverSide: {
        title: '4.2 后端集成（Server-Side）',
        subtitle: '示例：在支付前/支付成功后上报事件：',
        description:
          '从你的后端直接发送追踪事件，稳定性与可控性更高。',
        button:
          '可在用户点击“支付”按钮时触发，或在支付成功的回调中触发。',
      },
    },
    testing: {
      title: '5. 测试与校验',
      steps: [
        '完成集成后，在落地页 URL 末尾追加以下查询参数：?AHA_ORDER_ID=test_order_for_integration&AHA_CAMPAIGN_ID={campaignId}&AHA_CODE=test_code&utm_source=Aha',
        '按示例代码模拟一次购买事件上报，用于验证集成是否生效。',
      ],
      verifyButton: '验证集成',
      integrationSuccessful: '集成成功',
      integrationNotDetected: '未检测到集成',
    },
    troubleshooting: {
      title: '6. 故障排查',
      table: {
        symptom: '现象',
        causeAndFix: '原因与解决',
      },
      issues: {
        noEvents: {
          symptom: '未发送任何事件',
          cause:
            '脚本缺失、Client ID 无效，或未调用 {trackFunction}。',
        },
        trackerUndefined: {
          symptom: '{tracker} 未定义',
          cause: '脚本尚未加载完成——请包裹在 {onloadFunction} 中。',
        },
        redirectLosesParams: {
          symptom: '跳转后丢失追踪参数',
          cause: '请使用同域页面，或改用服务端集成以避免参数丢失。',
        },
        apiError: {
          symptom: '服务器 API 返回 401/403',
          cause: '请求头 {authHeader} 缺失/错误，或 API Key 不正确。',
        },
        paymentNotAttributed: {
          symptom: '支付未归因到分销/达人',
          cause:
            '检查分销参数与 AHA_ORDER_ID 是否在全流程被正确传递与保留。',
        },
        revenueNotSplit: {
          symptom: '收益分成未按预期执行',
          cause:
            '请在 Aha 平台检查各参与方与分成规则是否配置正确。',
        },
      },
    },
  },

  // Shopify 集成指南
  shopify: {
    overview: {
      title: '1. 概览',
      description:
        '本指南将帮助你在 Shopify 中集成 Aha 的追踪系统，实现准确归因、顺畅分成与自动结算（商家 ↔ 分销/达人）。',
    },
    prerequisites: {
      title: '2. 前置条件',
      table: {
        item: '项目',
        details: '说明',
      },
      items: {
        clientId: '你的 Client ID',
        apiKey: 'API Key（服务器端）',
        apiKeyDesc:
          '用于服务端调用的 Aha API Key（请妥善保密）。可在此生成你的 Key：',
        orderId: 'AHA_ORDER_ID',
        orderIdDesc:
          '当用户通过分销链接进入时，URL 会附带该 ID。请在支付流程中保持该值不丢失，以确保归因准确。',
      },
    },
    embedScript: {
      title: '3. 嵌入追踪脚本',
      steps: [
        '登录 Shopify 管理后台；',
        '打开主题编辑器：Online Store → Themes → Actions → Edit code；',
        '编辑布局文件：',
        '保存修改；',
        '验证：',
      ],
      subSteps: {
        openLayout: '打开 layout/theme.liquid；',
        pasteCode: '在 </head> 标签之前粘贴以下代码：',
        checkScript:
          '打开店铺任意页面，在控制台检查追踪脚本是否已加载；',
        checkNetwork:
          '在 Network 面板查看脚本是否被正确请求。',
      },
    },
    salesDataReporting: {
      title: '4. 销售数据上报',
      steps: [
        '登录 Shopify 管理后台；',
        '进入 Settings → Customer events → Add custom pixel；',
        '创建自定义 Pixel，并粘贴以下代码。',
      ],
    },
    testAndVerify: {
      title: '5. 测试与验证',
      steps: [
        '开启测试模式：',
        '在落地页 URL 末尾追加以下参数：?AHA_ORDER_ID=test_order_for_integration&AHA_CAMPAIGN_ID={campaignId}&AHA_CODE=test_code&utm_source=Aha',
        '使用下方测试支付信息完成一次购买；',
        '点击下方“验证集成”以确认是否集成成功。',
      ],
      subSteps: {
        goToAdmin: '进入 Shopify 管理后台；',
        navigateToPayments: '前往 Settings > Payments；',
        clickManage: '点击 Manage；',
        enableTestMode: '开启 Test mode（测试模式）。',
      },
      verifyButton: '验证集成',
      integrationSuccessful: '集成成功',
      integrationNotDetected: '未检测到集成',
    },
  },
};
